<template>
  <div
    v-if="activeThreadData"
    class="grid cursor-pointer grid-cols-10 items-center gap-x-3 px-4 py-2-5"
    :data-cy="activeThreadData.dataCy"
    @click="isOpen = !isOpen"
  >
    <div class="col-span-2 flex items-center space-x-1">
      <thread-icon :icon-info="activeThreadData.icon" />
      <thread-name-total :data="activeThreadData.namesAndCount" />
    </div>
    <div class="col-span-6 flex items-center">
      <template v-if="!isOpen">
        <thread-status :log="log" class="mr-2" />
        <thread-message
          class="mr-4 grow truncate"
          :message="activeThreadData.message"
        />
      </template>
      <thread-extra :log="log" />
    </div>
    <div class="col-span-2 grid grid-cols-7 items-center space-x-2">
      <div class="col-span-3">
        <business-name
          :id="activeThreadData.id"
          class="max-w-fit"
          :name="log.business_legal_name"
          :application-id="log.application_id"
          :type="log.type"
          @click.stop
        />
      </div>
      <date-time
        v-if="log.data?.created_at"
        :date="log.data.created_at"
        is-fixed
        show-time-if-today
        class="col-span-3 truncate text-xs"
      />
      <button
        class="flex h-4 w-4 items-center justify-center justify-self-end rounded hover:bg-grey-hover"
      >
        <dropdown-chevron
          :is-open="isOpen"
          :is-default-position="false"
          class="cursor-pointer text-headline"
        />
      </button>
    </div>
  </div>
  <div v-if="isOpen" class="border-t">
    <div class="m-4">
      <thread-item-task v-if="isTaskLog(log)" :log="log" />
      <thread-item-sms
        v-if="isSmsLog(log) && log.data?.sms_conversation_id"
        :log="log"
      />
      <thread-item-email v-if="isEmailLog(log)" :log="log" />
      <thread-item-note v-if="isNoteLog(log)" :log="log" />
      <thread-item-call v-if="isCallLog(log)" :log="log" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { CommunicationType } from "@/enums/communicationLogs";
import {
  isCallLog,
  isEmailLog,
  isNoteLog,
  isSmsLog,
  isTaskLog,
} from "@/helpers/communication";
import { useEmailLogInfo } from "@/hooks/communicationLogs";

import BusinessName from "@/components/communicationLogs/communication/emailThread/BusinessName.vue";
import ThreadExtra from "@/components/communicationLogs/communication/threads/ThreadExtra.vue";
import ThreadIcon from "@/components/communicationLogs/communication/threads/ThreadIcon.vue";
import ThreadMessage from "@/components/communicationLogs/communication/threads/ThreadMessage.vue";
import ThreadNameTotal from "@/components/communicationLogs/communication/threads/ThreadNameTotal.vue";
import ThreadStatus from "@/components/communicationLogs/communication/threads/ThreadStatus.vue";
import ThreadItemCall from "@/components/communicationLogs/communication/threads/openState/ThreadItemCall.vue";
import ThreadItemEmail from "@/components/communicationLogs/communication/threads/openState/ThreadItemEmail.vue";
import ThreadItemNote from "@/components/communicationLogs/communication/threads/openState/ThreadItemNote.vue";
import ThreadItemSms from "@/components/communicationLogs/communication/threads/openState/ThreadItemSms.vue";
import ThreadItemTask from "@/components/communicationLogs/communication/threads/openState/ThreadItemTask.vue";
import IconMessage from "@/components/icons/IconMessage.vue";
import IconTask from "@/components/icons/IconTask.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import IconDealNotes from "@/components/icons/dealNotes/DealNotes.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import DropdownChevron from "@/components/ui/DropdownChevron.vue";

import type {
  CallLog,
  CallLogData,
  CommunicationLogBase,
  CommunicationUser,
  EmailLog,
  NoteLog,
  SmsLog,
  TaskLog,
} from "@/models/communicationLogs";
import type { IUser } from "@/models/users";

interface Props {
  log: CommunicationLogBase;
}

const { log } = defineProps<Props>();

const { getters } = useStore();
const { t } = useI18n();

const isOpen = ref(false);

const user = computed<IUser | null>(() => getters["auth/user"]);

const activeThreadData = computed(() => {
  if (isEmailLog(log)) {
    return emailLogData(log);
  }

  if (isCallLog(log)) {
    return callLogData(log);
  }

  if (isNoteLog(log)) {
    return noteLogData(log);
  }

  if (isTaskLog(log)) {
    return taskLogData(log);
  }

  if (isSmsLog(log)) {
    return smsLogData(log);
  }

  return null;
});

const emailLogData = (log: EmailLog) => {
  const { iconInfo } = useEmailLogInfo(log.data);

  return {
    icon: iconInfo.value,
    namesAndCount: {
      names: getChattableNames(log.data),
      count: log.data.thread_messages_count || 0,
      linksClicked: log.data.links_clicked,
    },
    message: [log.data.subject, log.data.content_text]
      .filter(Boolean)
      .join(" - "),
    id: log.data.id,
    dataCy: "email-thread",
  };
};

const noteLogData = (log: NoteLog) => {
  return {
    icon: {
      icon: IconDealNotes,
      backgroundClass: "bg-flush-orange",
      viewBox: "0 0 16 16",
    },
    namesAndCount: {
      names: log.data?.user?.full_name || "",
      count: 0,
    },
    message: log.data?.comment,
    id: log.id,
    dataCy: "note-thread",
  };
};

const callLogData = (log: CallLog) => {
  return {
    icon: {
      icon: IconTelephone,
      backgroundClass: "bg-woods-green",
      viewBox: "0 0 16 16",
      isOutgoing: !!log.data?.is_outgoing,
      type: CommunicationType.call,
    },
    namesAndCount: {
      names: getCallNames(log.data),
      count: 0,
    },
    message: log.data?.body || t("COMMUNICATION.NO_CALL_NOTE"),
    id: log.id,
    dataCy: "call-thread",
  };
};

const smsLogData = (log: SmsLog) => {
  return {
    icon: {
      icon: IconMessage,
      backgroundClass: "bg-turquoise",
      viewBox: "0 0 16 16",
      isOutgoing: !!log.data?.is_outgoing,
    },
    namesAndCount: {
      names: getChattableNames(log.data),
      count: log.data.conversation_messages_count || 0,
    },
    message: log.data?.message,
    id: log.data?.id,
    dataCy: "sms-thread",
  };
};

const taskLogData = (log: TaskLog) => {
  return {
    icon: {
      icon: IconTask,
      backgroundClass: "bg-secondary-purple-1",
      viewBox: "0 0 12 12",
    },
    namesAndCount: {
      names: log.data?.assignee?.full_name || "",
      count: 0,
    },
    message: log.data?.body,
    id: log.id,
    dataCy: "task-thread",
  };
};

const getChattableNames = (data: {
  sender: CommunicationUser;
  receiver: CommunicationUser;
  thread_messages_count?: number;
}) => {
  const { sender, receiver, thread_messages_count } = data;

  if (thread_messages_count === 1) {
    return sender.id &&
      user.value &&
      sender.id?.toString() === user.value.id?.toString()
      ? t("COMMON.ME")
      : (sender.name || "").split(" ")[0];
  }

  const senderName = (sender.name || "").split(" ")[0];
  const receiverName = (receiver.name || "").split(" ")[0];

  return [senderName, receiverName].filter(Boolean).join(", ");
};

const getCallNames = (data: CallLogData) => {
  const { callee, user } = data;

  return [callee?.name?.split(" ")[0], user?.first_name]
    .filter(Boolean)
    .join(", ");
};
</script>
